import { useState } from "react";
import { useTranslation } from "react-i18next";
import { inviteStaff } from "../../api/platformStaff";
import { useApiError } from "../../hooks/useApiError";
import { NeuButton, NeuInput, NeuSelect } from "../../components/neu/Neu";
import { PermsPicker } from "./PermsPicker";
import { RoleTabs } from "./RoleTabs";

export const StaffInvite = ({ onDone }) => {
  const { t } = useTranslation();
  const describe = useApiError();
  const [form, setForm] = useState({ name: "", email: "", role: "platform_admin", language: "ar" });
  const [perms, setPerms] = useState(["support"]);
  const [state, setState] = useState({ busy: false, error: "", ok: "" });
  const isOwnerRole = form.role === "super_admin";

  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, error: "", ok: "" });
    try {
      await inviteStaff({ ...form, perms, email: form.email.trim().toLowerCase() });
      setState({ busy: false, error: "", ok: t("staff.invited") });
      setForm({ ...form, name: "", email: "" });
      onDone();
    } catch (err) {
      setState({ busy: false, error: describe(err), ok: "" });
    }
  };

  return (
    <form className="mt-4 grid gap-3 sm:grid-cols-4" onSubmit={submit} data-testid="staff-invite-form">
      <div className="sm:col-span-4">
        <RoleTabs value={form.role} onChange={(role) => setForm({ ...form, role })} />
      </div>
      <NeuInput
        data-testid="staff-name"
        placeholder={t("staff.name")}
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
      />
      <NeuInput
        data-testid="staff-email"
        type="email"
        dir="ltr"
        required
        placeholder={t("auth.email")}
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
      />
      <NeuSelect
        data-testid="staff-lang"
        value={form.language}
        onChange={(e) => setForm({ ...form, language: e.target.value })}
      >
        <option value="ar">{t("staff.langAr")}</option>
        <option value="en">{t("staff.langEn")}</option>
      </NeuSelect>
      <NeuButton variant="primary" type="submit" data-testid="staff-invite-submit" disabled={state.busy}>
        {state.busy ? t("app.loading") : t("staff.invite")}
      </NeuButton>
      {isOwnerRole ? (
        <p className="sm:col-span-4 text-xs font-semibold text-primary" data-testid="invite-owner-note">
          {t("staff.ownerNote")}
        </p>
      ) : (
        <div className="sm:col-span-4">
          <p className="label-xs mb-2">{t("staff.permsLabel")}</p>
          <PermsPicker value={perms} onChange={setPerms} testId="invite-perms" />
        </div>
      )}
      {state.error ? (
        <p className="text-sm font-semibold text-destructive sm:col-span-4" data-testid="staff-error">
          {state.error}
        </p>
      ) : null}
      {state.ok ? (
        <p className="text-sm font-semibold text-primary sm:col-span-4" data-testid="staff-ok">
          {state.ok}
        </p>
      ) : null}
    </form>
  );
};
